import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  Target, 
  PieChart,
  IndianRupee,
  Loader2,
  ExternalLink,
  Download,
  FileText,
  FileSpreadsheet
} from "lucide-react";
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

export const PortfolioPage = () => {
  const { token } = useAuth();
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newHolding, setNewHolding] = useState({
    scheme_code: "",
    scheme_name: "",
    units: "",
    purchase_nav: "",
    purchase_date: ""
  });

  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setHoldings(response.data.holdings || []);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const exportPortfolio = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/portfolio/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `portfolio_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Portfolio exported to ${format.toUpperCase()}!`);
    } catch (error) {
      toast.error("Failed to export portfolio");
    } finally {
      setExporting(false);
    }
  };

  const addHolding = async () => {
    if (!newHolding.scheme_code || !newHolding.scheme_name || !newHolding.units || !newHolding.purchase_nav) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await axios.post(`${API}/portfolio`, {
        ...newHolding,
        units: parseFloat(newHolding.units),
        purchase_nav: parseFloat(newHolding.purchase_nav)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Holding added successfully!");
      setIsAddDialogOpen(false);
      setNewHolding({ scheme_code: "", scheme_name: "", units: "", purchase_nav: "", purchase_date: "" });
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to add holding");
    }
  };

  const removeHolding = async (holdingId) => {
    try {
      await axios.delete(`${API}/portfolio/${holdingId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Holding removed");
      fetchPortfolio();
    } catch (error) {
      toast.error("Failed to remove holding");
    }
  };

  // Calculate portfolio stats
  const totalInvested = holdings.reduce((sum, h) => sum + (h.units * h.purchase_nav), 0);
  const currentValue = totalInvested * 1.15; // Demo: 15% growth
  const totalGain = currentValue - totalInvested;
  const gainPercent = totalInvested > 0 ? ((totalGain / totalInvested) * 100).toFixed(2) : 0;

  // Pie chart data
  const pieData = holdings.length > 0 
    ? holdings.map((h, i) => ({
        name: h.scheme_name?.split(' ').slice(0, 2).join(' ') || `Fund ${i + 1}`,
        value: h.units * h.purchase_nav,
        color: `hsl(var(--chart-${(i % 5) + 1}))`
      }))
    : [
        { name: "Equity", value: 60, color: "hsl(var(--chart-1))" },
        { name: "Debt", value: 25, color: "hsl(var(--chart-2))" },
        { name: "Hybrid", value: 15, color: "hsl(var(--chart-3))" },
      ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="portfolio-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              My <span className="gold-text">Portfolio</span>
            </h1>
            <p className="text-muted-foreground">Track and manage your mutual fund investments</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-white/10" disabled={exporting || holdings.length === 0} data-testid="export-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border">
                <DropdownMenuItem onClick={() => exportPortfolio('pdf')} className="cursor-pointer" data-testid="export-pdf">
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportPortfolio('excel')} className="cursor-pointer" data-testid="export-excel">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                  Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-holding-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Holding
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Add New Holding</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Scheme Code</Label>
                  <Input
                    placeholder="e.g., 119551"
                    value={newHolding.scheme_code}
                    onChange={(e) => setNewHolding({ ...newHolding, scheme_code: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="scheme-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scheme Name</Label>
                  <Input
                    placeholder="Fund Name"
                    value={newHolding.scheme_name}
                    onChange={(e) => setNewHolding({ ...newHolding, scheme_name: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="scheme-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Units</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newHolding.units}
                      onChange={(e) => setNewHolding({ ...newHolding, units: e.target.value })}
                      className="bg-secondary/50 border-white/10"
                      data-testid="units-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Purchase NAV</Label>
                    <Input
                      type="number"
                      placeholder="50.00"
                      value={newHolding.purchase_nav}
                      onChange={(e) => setNewHolding({ ...newHolding, purchase_nav: e.target.value })}
                      className="bg-secondary/50 border-white/10"
                      data-testid="nav-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={newHolding.purchase_date}
                    onChange={(e) => setNewHolding({ ...newHolding, purchase_date: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="date-input"
                  />
                </div>
                <Button onClick={addHolding} className="w-full btn-primary" data-testid="submit-holding-btn">
                  Add to Portfolio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Invested</p>
              <p className="font-mono text-2xl font-bold" data-testid="total-invested">
                {formatCurrency(totalInvested || 250000)}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Current Value</p>
              <p className="font-mono text-2xl font-bold" data-testid="current-value">
                {formatCurrency(currentValue || 287500)}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Gain</p>
              <p className="font-mono text-2xl font-bold text-green-500" data-testid="total-gain">
                +{formatCurrency(totalGain || 37500)}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Holdings</p>
              <p className="font-mono text-2xl font-bold" data-testid="holdings-count">
                {holdings.length}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Holdings List */}
          <Card className="glass-card lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Holdings</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                </div>
              ) : holdings.length > 0 ? (
                <div className="space-y-4">
                  {holdings.map((holding, index) => {
                    const investedValue = holding.units * holding.purchase_nav;
                    const currentVal = investedValue * 1.15; // Demo
                    const gain = currentVal - investedValue;
                    const gainPct = ((gain / investedValue) * 100).toFixed(2);

                    return (
                      <div
                        key={holding.holding_id || index}
                        className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl"
                        data-testid={`holding-${index}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <Link to={`/research/${holding.scheme_code}`} className="font-medium hover:text-primary flex items-center gap-1">
                                {holding.scheme_name}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                              <p className="text-xs text-muted-foreground">
                                {holding.units} units @ ₹{holding.purchase_nav}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => removeHolding(holding.holding_id)}
                              data-testid={`remove-holding-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">
                              Invested: <span className="font-mono">{formatCurrency(investedValue)}</span>
                            </span>
                            <span className="text-muted-foreground">
                              Current: <span className="font-mono">{formatCurrency(currentVal)}</span>
                            </span>
                            <span className={`flex items-center gap-1 font-mono ${gain >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {gain >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {gain >= 0 ? '+' : ''}{gainPct}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No Holdings Yet</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Add your first mutual fund holding to start tracking
                  </p>
                  <Button variant="outline" className="border-white/10" asChild>
                    <Link to="/research">Browse Funds</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Allocation Chart */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Allocation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => holdings.length > 0 ? formatCurrency(value) : `${value}%`}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-4">
                {pieData.slice(0, 5).map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
                      <span className="text-muted-foreground truncate max-w-[120px]">{item.name}</span>
                    </div>
                    <span className="font-mono">
                      {holdings.length > 0 ? formatCurrency(item.value) : `${item.value}%`}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};
